import { Link } from "react-router-dom";
import { useCallback } from "react";
import {MapPin, Briefcase, Calendar, Euro, ArrowRight, Clock, Heart} from 'lucide-react';
import toast from "react-hot-toast";
import { Job, CONTRACT_LABELS } from "@/data/jobs";
import { buildJobPath } from "@/lib/jobUrl";
import { useFavoriteJobs } from "@/hooks/useFavoriteJobs";

interface JobCardProps {
  job: Job;
}

/**
 * JobCard — Refonte éditoriale premium (Phase 2).
 *
 * Direction artistique :
 *  - SANS BORDURE : fond blanc cassé chaud (#FAF7F2) qui se détache du fond global
 *  - Accent latéral épais (4px) BNSSA rouge / MNS bleu, toujours visible — signature univers
 *  - Logo employeur intégré comme un tampon éditorial (rond, position absolute en haut-droite)
 *  - Hiérarchie typo magazine : eyebrow couleur silo, H3 serré, méta-données en tabular nums
 *  - Hover : élévation translateY(-2px) + glow chaud subtil, sans bordure visible
 *  - Footer minimal type Linear : timestamp ping + CTA flèche (sans soulignement)
 */

const formatPublishedAgo = (iso: string): { label: string; isFresh: boolean; isVeryFresh: boolean } => {
  if (!iso) return { label: "", isFresh: false, isVeryFresh: false };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { label: "", isFresh: false, isVeryFresh: false };

  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let label = "";
  if (diffMs < 0) {
    label = d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  } else if (diffMin < 1) {
    label = "À l'instant";
  } else if (diffH < 1) {
    label = `Il y a ${diffMin} min`;
  } else if (diffH < 24) {
    label = `Il y a ${diffH} h`;
  } else if (diffDays === 1) {
    label = "Hier";
  } else if (diffDays < 7) {
    label = `Il y a ${diffDays} jours`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    label = weeks === 1 ? "Il y a 1 semaine" : `Il y a ${weeks} semaines`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    label = months === 1 ? "Il y a 1 mois" : `Il y a ${months} mois`;
  } else {
    const years = Math.floor(diffDays / 365);
    label = years === 1 ? "Il y a 1 an" : `Il y a ${years} ans`;
  }

  return {
    label,
    isFresh: diffDays < 7,
    isVeryFresh: diffDays < 2,
  };
};

export default function JobCard({ job }: JobCardProps) {
  const isBNSSA = job.category === "BNSSA";
  const accentColor = isBNSSA ? "#dc2626" : "#2563eb";
  const accentSoft = isBNSSA ? "rgba(220,38,38,0.08)" : "rgba(37,99,235,0.08)";
  const jobPath = buildJobPath(job);

  const { isFavorite: getIsFavorite, toggleFavorite: toggleFav } = useFavoriteJobs();
  const isFavorite = getIsFavorite(job.id);

  const toggleFavorite = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const added = toggleFav(job.id);
      toast.success(added ? "Ajouté à vos favoris" : "Retiré des favoris", {
        duration: 1800,
        icon: added ? "❤️" : "💔",
      });
    },
    [job.id, toggleFav]
  );

  const formatMissionDate = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  };

  const isAutoEntrepreneur = job.contractType === "auto-entrepreneur";
  const isInterim = job.contractType === "interim";
  const isMissionContract = isAutoEntrepreneur || isInterim;
  const hasMissionInfo = isMissionContract && (job.missionDate || job.missionStartTime || job.missionEndTime);

  // Mission pourvue : un candidat a été retenu (AE/Intérim uniquement).
  // → tampon rouge + carte grisée + liens désactivés (pas d'accès à l'annonce).
  const isFilled = isMissionContract && job.isFilled === true;

  const published = formatPublishedAgo(job.publishedAt);

  return (
    <article
      className="group relative rounded-2xl overflow-hidden ease-premium"
      style={{
        // Fond blanc cassé chaud — signature éditoriale, pas de bordure
        backgroundColor: "#ffffff",
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
        transition:
          "transform 350ms cubic-bezier(0.16,1,0.3,1), box-shadow 350ms cubic-bezier(0.16,1,0.3,1)",
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.boxShadow = `0 1px 2px rgba(15,23,42,0.05), 0 16px 40px -12px rgba(15,23,42,0.14), 0 0 0 1px ${accentSoft}`;
        el.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.boxShadow =
          "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)";
        el.style.transform = "translateY(0)";
      }}
    >
      {/* Tampon « Mission pourvue » centré en diagonale (visuel non bloquant : la carte reste comme les autres, non grisée) */}
      {isFilled && (
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
          <span
            className="select-none uppercase font-extrabold tracking-[0.12em] text-[15px] sm:text-[18px] px-5 py-2 rounded-lg"
            style={{
              color: "#dc2626",
              border: "3px solid #dc2626",
              background: "rgba(255,255,255,0.94)",
              transform: "rotate(-12deg)",
              boxShadow: "0 4px 14px rgba(220,38,38,0.22)",
              textShadow: "0 1px 0 rgba(255,255,255,0.6)",
            }}
          >
            Mission pourvue
          </span>
        </div>
      )}
      {/* Accent bar horizontale en haut — signature BNSSA/MNS, toujours visible */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-1"
        style={{
          background: isBNSSA
            ? "linear-gradient(90deg, #ef4444 0%, #b91c1c 100%)"
            : "linear-gradient(90deg, #3b82f6 0%, #1e40af 100%)",
        }}
      />

      {/* Bouton favori — top right, sans bordure visible */}
      <button
        type="button"
        onClick={toggleFavorite}
        aria-pressed={isFavorite}
        aria-label={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
        title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
        className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-sm transition-all duration-200 ${
          isFavorite
            ? "bg-red-50/90 text-red-500"
            : "bg-white/70 text-gray-400 hover:text-red-500 hover:bg-red-50"
        }`}
        style={{ boxShadow: "0 2px 8px rgba(15,23,42,0.06)" }}
      >
        <Heart
          className={`w-[18px] h-[18px] transition-transform duration-200 ${isFavorite ? "fill-current scale-110" : "fill-none"}`}
          strokeWidth={2}
        />
      </button>

      <div className="p-5">
        {/* Header : eyebrow couleur silo + tampon logo en haut-droite */}
        <div className="flex items-start gap-3 mb-3.5" style={{ minHeight: "84px" }}>
          <div className="flex-1 min-w-0 pr-10">
            {/* Eyebrow éditorial : catégorie + type contrat */}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span
                className="text-[10.5px] font-bold uppercase tracking-[0.18em]"
                style={{ color: accentColor }}
              >
                {job.category}
              </span>
              <span className="w-1 h-1 rounded-full bg-gray-300" aria-hidden="true" />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                {CONTRACT_LABELS[job.contractType]}
              </span>
              {isAutoEntrepreneur && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider bg-amber-100/70 text-amber-800">
                  AE
                </span>
              )}
            </div>

            {/* Title — éditorial serré (toujours cliquable, même si la mission est pourvue) */}
            <h3 className="text-[15.5px] font-bold text-[#0b1220] leading-[1.25] mb-1 tracking-[-0.015em] line-clamp-2">
              <Link
                to={jobPath}
                rel={isFilled ? "nofollow" : "dofollow"}
                className="hover:opacity-80 transition-opacity"
                aria-label={`Voir l'offre : ${job.title}`}
              >
                {job.title}
              </Link>
            </h3>
            <p className="text-[12.5px] text-gray-500 font-medium truncate">{job.employer}</p>
          </div>

          {/* Slot logo — tampon éditeur, position absolute pour ne pas perturber le flow */}
          <div
            data-logo-slot
            aria-hidden={job.logo ? undefined : true}
            className="flex-shrink-0 w-12 h-12 rounded-xl bg-white overflow-hidden flex items-center justify-center mt-7"
            style={{
              visibility: job.logo ? "visible" : "hidden",
              boxShadow: "0 1px 3px rgba(15,23,42,0.08), inset 0 0 0 1px rgba(15,23,42,0.04)",
            }}
          >
            {job.logo && (
              <img
                src={job.logo}
                alt={`Logo ${job.employer}`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-contain p-1"
                onError={(e) => {
                  const slot = e.currentTarget.closest("[data-logo-slot]") as HTMLElement | null;
                  if (slot) slot.style.visibility = "hidden";
                }}
              />
            )}
          </div>
        </div>

        {/* Meta info */}
        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2 text-[12.5px] text-gray-500">
            <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" strokeWidth={2.2} />
            <span className="truncate">
              {job.location} · {job.department}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[12.5px] text-gray-500">
            <Briefcase className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" strokeWidth={2.2} />
            <span className="truncate">{CONTRACT_LABELS[job.contractType]}</span>
          </div>

          {/* Mission unique (AE/Intérim) */}
          {hasMissionInfo && job.missionDate && (
            <div className="flex items-center gap-2 text-[12.5px] text-gray-700 font-semibold">
              <Calendar className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" strokeWidth={2.2} />
              <span className="truncate">
                {formatMissionDate(job.missionDate)}
                {job.missionStartTime && job.missionEndTime && (
                  <span className="font-medium text-gray-500">
                    {" "}
                    · {job.missionStartTime}–{job.missionEndTime}
                  </span>
                )}
                {typeof job.missionHours === "number" && (
                  <span className="font-medium text-gray-500"> · {job.missionHours}h</span>
                )}
              </span>
            </div>
          )}
          {hasMissionInfo && !job.missionDate && job.missionStartTime && job.missionEndTime && (
            <div className="flex items-center gap-2 text-[12.5px] text-gray-500">
              <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" strokeWidth={2.2} />
              <span className="truncate">
                {job.missionStartTime}–{job.missionEndTime}
              </span>
            </div>
          )}

          {/* Rémunération — tabular nums pour précision */}
          {isInterim && typeof job.missionPrice === "number" ? (
            <div className="flex items-center gap-2 text-[12.5px] font-bold text-[#0b1220] tabular">
              <Euro className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" strokeWidth={2.2} />
              <span className="truncate">
                {job.missionPrice} € <span className="font-medium text-gray-500">/ mission</span>
              </span>
            </div>
          ) : job.salary || job.hourlyRate ? (
            <div className="flex items-center gap-2 text-[12.5px] font-bold text-[#0b1220] tabular">
              <Euro className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" strokeWidth={2.2} />
              <span className="truncate">{job.salary || job.hourlyRate}</span>
            </div>
          ) : null}
        </div>

        {/* Description */}
        <p className="text-[12.5px] text-gray-500 leading-[1.55] line-clamp-2 mb-4">
          {job.description}
        </p>

        {/* Footer — séparateur ultra-fin via background, pas de border-top */}
        <div
          className="flex items-center justify-between pt-3.5"
          style={{ boxShadow: "inset 0 1px 0 rgba(15,23,42,0.06)" }}
        >
          <span
            className={`flex items-center gap-1.5 text-[11.5px] font-medium tabular ${
              published.isVeryFresh
                ? "text-emerald-600"
                : published.isFresh
                ? "text-emerald-600/85"
                : "text-gray-400"
            }`}
            title={`Publié le ${new Date(job.publishedAt).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}`}
          >
            {published.isFresh ? (
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            ) : (
              <Calendar className="w-3.5 h-3.5" strokeWidth={2.2} />
            )}
            {published.label}
          </span>
          <Link
            to={jobPath}
            rel="nofollow"
            className="inline-flex items-center gap-1 text-[12.5px] font-bold transition-all duration-200 group-hover:gap-1.5"
            style={{ color: isFilled ? "#6b7280" : accentColor }}
            aria-label={isFilled ? `Voir l'annonce (mission pourvue) : ${job.title}` : `Voir l'offre : ${job.title}`}
          >
            {isFilled ? "Voir l'annonce" : "Voir l'offre"}
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
          </Link>
        </div>
      </div>
    </article>
  );
}
